import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  listPrinters, getPrinterStatus,
} from '@dme/ticket-print';
import { initializeQzKeys, isQzInitialized } from 'utils/utils';
import translationKeys from 'translations/translationKeys';
import crypto from 'crypto-js';
import config from '../../config';

const STORAGE_KEY = 'printerConfig';

const defaultConfig = {
  printerName: 'POS80 Printer',
  cpl: 48,
  count: 1,
  qzPrintingEnabled: true,
};

const {
  msg273, msg274, msg275,
} = translationKeys;

function loadFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    if (!raw) return defaultConfig;
    const decryptedData = crypto.AES.decrypt(raw, config.ENCRYPTION_KEY);
    const parsed = JSON.parse(decryptedData.toString(crypto.enc.Utf8));
    return {
      printerName: parsed.printerName || defaultConfig.printerName,
      cpl: parseInt(parsed.cpl || defaultConfig.cpl, 10),
      count: parseInt(parsed.count || defaultConfig.count, 10),
      qzPrintingEnabled: parsed.qzPrintingEnabled === true,
    };
  } catch {
    return defaultConfig;
  }
}

function saveToStorage(printerConfig) {
  try {
    const data = JSON.stringify(printerConfig);
    const encryptedData = crypto.AES.encrypt(data, config.ENCRYPTION_KEY).toString();
    sessionStorage.setItem(STORAGE_KEY, encryptedData);
  } catch {
    // ignore storage errors
  }
}

// ── Thunks ────────────────────────────────────────────────────────────────────

/**
 * Fetch all printers visible to QZ Tray, then enrich each with its live status.
 * Returns: Array<{
 * name: string,
 * isDefault: boolean,
 * isReady: boolean,
 * statusText: string,
 * severity: string
 * }>
 */
export const fetchPrinters = createAsyncThunk(
  'printerConfig/fetchPrinters',
  async (_, thunkAPI) => {
    try {
      if (!isQzInitialized()) {
        const initialized = await initializeQzKeys();

        if (!initialized) {
          return thunkAPI.rejectWithValue(msg273);
        }
      }

      const printers = await listPrinters();

      // Check status for each printer in parallel (timeout 4 s each)
      const enriched = printers.map((p) => ({
        name: p.name,
        isDefault: p.isDefault ?? false,
      }));

      return enriched;
    } catch (error) {
      console.error(error?.message);
      return thunkAPI.rejectWithValue(msg274);
    }
  },
);

/**
 * Check the status of a single named printer.
 */
export const checkPrinterStatus = createAsyncThunk(
  'printerConfig/checkPrinterStatus',
  async (printerName, thunkAPI) => {
    try {
      const status = await getPrinterStatus({ printerName, timeoutMs: 5000 });
      return {
        name: printerName,
        isReady: status.isReady,
        statusText: status.statusText,
        severity: status.severity,
      };
    } catch (error) {
      console.error(error?.message);
      return thunkAPI.rejectWithValue(msg275);
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  config: loadFromStorage(),

  // Available printers list
  printers: [],
  printersLoading: false,
  printersError: null,

  // Single-printer status check
  statusChecking: false,
  statusError: null,
  lastCheckedPrinter: null, // { name, isReady, statusText, severity }

  // Persist feedback
  saveSuccess: false,
};

const printerConfigSlice = createSlice({
  name: 'printerConfig',
  initialState,
  reducers: {
    /** Update one or more config fields and immediately persist. */
    updateConfig: (state, { payload }) => {
      const next = { ...state.config, ...payload };
      saveToStorage(next);
      return { ...state, config: next, saveSuccess: true };
    },

    /** Replace the entire config and persist. */
    setConfig: (state, { payload }) => {
      const next = {
        printerName: payload.printerName || defaultConfig.printerName,
        cpl: parseInt(payload.cpl || defaultConfig.cpl, 10),
        count: parseInt(payload.count || defaultConfig.count, 10),
        qzPrintingEnabled: payload.qzPrintingEnabled === true,
      };
      saveToStorage(next);
      return { ...state, config: next, saveSuccess: true };
    },

    /** Re-read config from localStorage (e.g. after an external write). */
    reloadConfig: (state) => ({ ...state, config: loadFromStorage() }),

    clearSaveSuccess: (state) => ({ ...state, saveSuccess: false }),

    clearStatusError: (state) => ({ ...state, statusError: null, printersError: null }),
  },

  extraReducers: (builder) => {
    // fetchPrinters
    builder
      .addCase(fetchPrinters.pending, (state) => ({
        ...state,
        printersLoading: true,
        printersError: null,
      }))
      .addCase(fetchPrinters.fulfilled, (state, { payload }) => ({
        ...state,
        printersLoading: false,
        printers: payload,
      }))
      .addCase(fetchPrinters.rejected, (state, { payload }) => ({
        ...state,
        printersLoading: false,
        printersError: payload,
        printers: [],
      }));

    // checkPrinterStatus
    builder
      .addCase(checkPrinterStatus.pending, (state) => ({
        ...state,
        statusChecking: true,
        statusError: null,
        lastCheckedPrinter: null,
      }))
      .addCase(checkPrinterStatus.fulfilled, (state, { payload }) => ({
        ...state,
        statusChecking: false,
        lastCheckedPrinter: payload,
        // Update the matching printer entry in the list if present
        printers: state.printers.map((p) => (p.name === payload.name ? { ...p, ...payload } : p)),
      }))
      .addCase(checkPrinterStatus.rejected, (state, { payload }) => ({
        ...state,
        statusChecking: false,
        statusError: payload,
      }));
  },
});

export const {
  updateConfig,
  setConfig,
  reloadConfig,
  clearSaveSuccess,
  clearStatusError,
} = printerConfigSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectPrinterConfig = (state) => state.printerConfig.config;
export const selectPrinters = (state) => state.printerConfig.printers;
export const selectPrintersLoading = (state) => state.printerConfig.printersLoading;
export const selectPrintersError = (state) => state.printerConfig.printersError;
export const selectStatusChecking = (state) => state.printerConfig.statusChecking;
export const selectStatusError = (state) => state.printerConfig.statusError;
export const selectLastCheckedPrinter = (state) => state.printerConfig.lastCheckedPrinter;
export const selectSaveSuccess = (state) => state.printerConfig.saveSuccess;

export default printerConfigSlice.reducer;
